import { toast } from "@/components/ui";
import type { ApiError } from "@/hooks/useApi";
import type { AxiosError } from "axios";

interface MultipleEncountersErrorResponse {
  detail: {
    message: string;
    otherEncounters: Array<{
      practitionerFullName: string | null;
      encounterId: string;
      professionalUserId: number;
    }>;
  };
}

const formatMultipleEncountersMessage = (
  otherEncounters: MultipleEncountersErrorResponse["detail"]["otherEncounters"],
): string => {
  const providerNames = otherEncounters
    .map((e) => e.practitionerFullName)
    .filter(Boolean);

  if (providerNames.length === 0) {
    return "This patient has additional open visits with other providers. Medical history cannot be updated while more than one provider is charting.";
  }

  if (providerNames.length === 1) {
    return `This patient has an additional open visit with ${providerNames[0]}. Medical history cannot be updated while more than one provider is charting.`;
  }

  return `This patient has additional open visits with ${providerNames.join(", ")}. Medical history cannot be updated while more than one provider is charting.`;
};

/**
 * Wraps an onError callback to handle 423 (multiple open encounters) errors
 * with a user-friendly toast message.
 */
export const withMultipleEncountersHandler = (
  onError?: (error: ApiError) => void,
) => {
  return (error: AxiosError<ApiError>) => {
    if (error.response?.status === 423) {
      const data = error.response
        ?.data as unknown as MultipleEncountersErrorResponse;
      toast.error(
        formatMultipleEncountersMessage(data?.detail?.otherEncounters || []),
      );
      return;
    }
    // useApiMutation expects every error to be an ApiError, which is not what
    //
    onError?.(error as unknown as ApiError);
  };
};
